// Compose and send email from inside the app, through the user's own Microsoft
// mailbox.
//
// Two behaviours are deliberate and worth not "simplifying" later:
//
//  1. An expired Microsoft authorization NEVER closes this window. The draft
//     stays exactly as typed, a reconnect banner appears, and the same Send
//     button works again afterwards. Losing a half-written email to a token
//     refresh is the single most annoying way this feature can fail.
//
//  2. Bulk sends go out as one message per recipient, so candidates never see
//     each other's addresses. The window says so, because a recruiter about to
//     mail forty people deserves to know which it is.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from './ui/Icon';
import {
  mailApi,
  type EmailTemplate,
  type MailConnectionStatus,
  type MailSendResult,
  type SendEmailPayload,
} from '../lib/api';

export interface ComposerRecipient {
  address: string;
  candidateId?: string | null;
  name?: string | null;
}

interface Props {
  recipients: ComposerRecipient[];
  open: boolean;
  onClose: () => void;
  onSent?: (result: MailSendResult) => void;
  /** Offer "attach this candidate's resume" — only valid for a single recipient. */
  allowResumeAttachment?: boolean;
  defaultSubject?: string;
  defaultBody?: string;
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};
const sheet: React.CSSProperties = {
  background: 'var(--surface)', borderRadius: 14, width: 'min(760px, 100%)',
  maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,.3)',
};
const pad: React.CSSProperties = { padding: 20 };
const label: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--text)', display: 'block', marginBottom: 4 };
const input: React.CSSProperties = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--border-strong)',
  borderRadius: 8, fontSize: 13, fontFamily: 'inherit',
};
const btn: React.CSSProperties = {
  padding: '9px 16px', background: 'var(--text-strong)', color: 'var(--surface)', border: 'none',
  borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
};
const ghost: React.CSSProperties = {
  ...btn, background: 'var(--surface-subtle)', color: 'var(--text)', border: '1px solid var(--border)', fontWeight: 600,
};
const noticeBase: React.CSSProperties = {
  padding: '10px 12px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.5, marginBottom: 12,
};
const warn: React.CSSProperties = { ...noticeBase, background: 'var(--warn-tint)', border: '1px solid var(--warn-border)', color: '#78350f' };
const bad: React.CSSProperties = { ...noticeBase, background: 'var(--danger-tint)', border: '1px solid var(--danger-border)', color: '#7f1d1d' };
const good: React.CSSProperties = { ...noticeBase, background: 'var(--good-tint)', border: '1px solid var(--good-border)', color: '#14532d' };
const info: React.CSSProperties = { ...noticeBase, background: 'var(--info-tint)', border: '1px solid var(--info-border)', color: 'var(--brand-strong)' };

/** Escape then convert newlines, so a plain-text draft arrives as readable HTML. */
function toHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped.split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
}

/** Fill {{first_name}}-style placeholders for a single, known recipient. */
function applyMergeFields(text: string, r: ComposerRecipient | undefined): string {
  if (!r) return text;
  const first = (r.name ?? '').trim().split(/\s+/)[0] ?? '';
  return text
    .replace(/\{\{\s*first_name\s*\}\}/gi, first)
    .replace(/\{\{\s*name\s*\}\}/gi, r.name ?? '')
    .replace(/\{\{\s*email\s*\}\}/gi, r.address);
}

export default function EmailComposer({
  recipients, open, onClose, onSent,
  allowResumeAttachment = false, defaultSubject = '', defaultBody = '',
}: Props) {
  const [status, setStatus] = useState<MailConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);

  const [from, setFrom] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [attachResume, setAttachResume] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<{ message: string; reconnect: boolean } | null>(null);
  const [result, setResult] = useState<MailSendResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const single = recipients.length === 1;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await mailApi.status();
      setStatus(r.data);
      // Only seed From on first load — never stomp a deliberate choice of
      // shared mailbox when the status refreshes after a reconnect.
      setFrom(prev => prev || r.data.connection?.mailbox || '');
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadStatus();
    mailApi.templates().then(r => setTemplates(r.data.templates)).catch(() => setTemplates([]));
  }, [open, loadStatus]);

  // The consent popup lands back on this origin; re-read status when the tab
  // regains focus so the banner clears without a manual refresh.
  useEffect(() => {
    if (!open) return;
    const onFocus = () => { void loadStatus(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [open, loadStatus]);

  const totalAttachmentBytes = useMemo(() => files.reduce((s, f) => s + f.size, 0), [files]);
  const overLimit = totalAttachmentBytes > 3 * 1024 * 1024;

  async function connect() {
    try {
      const r = await mailApi.connect(window.location.pathname + window.location.search);
      // A popup keeps the draft alive in this tab. Falling back to a full
      // redirect would lose it, so only do that if the popup is blocked.
      const popup = window.open(r.data.authorize_url, 'ms-connect', 'width=520,height=680');
      if (!popup) window.location.href = r.data.authorize_url;
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      setError({ message: err.response?.data?.message ?? 'Could not start the Microsoft connection.', reconnect: false });
    }
  }

  function applyTemplate(id: string) {
    const t = templates.find(x => String(x.id) === id);
    if (!t) return;
    const target = single ? recipients[0] : undefined;
    if (t.subject) setSubject(applyMergeFields(t.subject, target));
    setBody(applyMergeFields(t.content, target));
  }

  async function send() {
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const payload: SendEmailPayload = {
        from: from || null,
        to: recipients.map(r => ({ address: r.address, candidate_id: r.candidateId ?? null, name: r.name ?? null })),
        cc: cc.split(/[,;]/).map(s => s.trim()).filter(Boolean),
        bcc: bcc.split(/[,;]/).map(s => s.trim()).filter(Boolean),
        subject,
        body_html: toHtml(single ? applyMergeFields(body, recipients[0]) : body),
        attach_resume: attachResume,
        individual: true,
      };
      const r = await mailApi.send(payload, files);
      setResult(r.data);
      onSent?.(r.data);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: string; error?: string; reconnect_required?: boolean } } };
      const data = err.response?.data;
      setError({
        message: data?.message ?? 'The email could not be sent.',
        // The draft stays put; the banner offers the one action that helps.
        reconnect: !!data?.reconnect_required || data?.error === 'not_connected',
      });
      if (data?.reconnect_required || data?.error === 'not_connected') void loadStatus();
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;

  const notConnected = !loading && status && !status.connected;
  const configProblem = !loading && status && !status.configured;

  return (
    <div style={overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label="Compose email">
      <div style={sheet} onClick={e => e.stopPropagation()}>
        <div style={{ ...pad, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: 'var(--text-strong)' }}>
            {single ? 'Send email' : `Send email to ${recipients.length} candidates`}
          </h2>
          <button style={{ ...ghost, padding: '4px 10px' }} onClick={onClose}>Close</button>
        </div>

        <div style={pad}>
          {loading && <div style={info}>Checking your Microsoft connection…</div>}

          {configProblem && (
            <div style={bad}>
              <strong>Email sending isn’t configured yet.</strong>
              <div style={{ marginTop: 4 }}>{status?.config_problem}</div>
            </div>
          )}

          {notConnected && !configProblem && (
            <div style={warn}>
              <strong>
                {status?.reconnect_required
                  ? 'Your Microsoft authorization has expired.'
                  : 'Connect your Microsoft account to send email.'}
              </strong>
              <div style={{ marginTop: 4 }}>
                {status?.reconnect_required
                  ? 'Reconnect to keep sending. Anything you have typed here is kept.'
                  : 'Email is sent from your own mailbox and appears in your Sent folder.'}
                {status?.connection?.last_error ? ` (${status.connection.last_error})` : ''}
              </div>
              <button style={{ ...btn, marginTop: 8 }} onClick={connect}>
                {status?.reconnect_required ? 'Reconnect Microsoft' : 'Connect Microsoft'}
              </button>
            </div>
          )}

          {error && (
            <div style={bad}>
              {error.message}
              {error.reconnect && (
                <div style={{ marginTop: 8 }}>
                  <button style={btn} onClick={connect}>Reconnect Microsoft</button>
                  <span style={{ marginLeft: 8 }}>Your draft is kept — press Send again once you’re reconnected.</span>
                </div>
              )}
            </div>
          )}

          {result && (
            <div style={result.status === 'sent' ? good : result.status === 'partial' ? warn : bad}>
              <strong>
                {result.status === 'sent' && `Sent to ${result.sent} recipient${result.sent === 1 ? '' : 's'}.`}
                {result.status === 'partial' && `Partially delivered — ${result.sent} sent, ${result.failed} failed.`}
                {result.status === 'failed' && 'Not sent.'}
              </strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {result.results.filter(r => r.status !== 'sent').map(r => (
                  <li key={r.communicationId}>{r.address} — {r.errorMessage ?? r.status}</li>
                ))}
              </ul>
            </div>
          )}

          {/* From */}
          <div style={{ marginBottom: 12 }}>
            <label style={label} htmlFor="mail-from">From</label>
            {status && status.allowed_senders.length > 1 ? (
              <select id="mail-from" style={input} value={from} onChange={e => setFrom(e.target.value)}>
                {status.allowed_senders.map(m => (
                  <option key={m} value={m}>
                    {m}{m === status.connection?.mailbox ? ' (your mailbox)' : ' (shared)'}
                  </option>
                ))}
              </select>
            ) : (
              <input id="mail-from" style={{ ...input, background: 'var(--surface-sunken)' }} value={from} readOnly />
            )}
          </div>

          {/* To */}
          <div style={{ marginBottom: 12 }}>
            <label style={label}>To</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {recipients.slice(0, 12).map(r => (
                <span key={r.address} style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border)', borderRadius: 999, padding: '3px 10px', fontSize: 12 }}>
                  {r.name ? `${r.name} · ` : ''}{r.address}
                </span>
              ))}
              {recipients.length > 12 && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>+{recipients.length - 12} more</span>
              )}
            </div>
            {!single && (
              <div style={{ ...info, marginTop: 8, marginBottom: 0 }}>
                Each candidate gets their own separate message. They will not see one another’s
                email addresses, and each one gets its own delivery status.
              </div>
            )}
          </div>

          {!showCc && (
            <button style={{ ...ghost, marginBottom: 12, padding: '4px 10px', fontSize: 12 }} onClick={() => setShowCc(true)}>
              Add Cc / Bcc
            </button>
          )}
          {showCc && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={label} htmlFor="mail-cc">Cc</label>
                <input id="mail-cc" style={input} value={cc} onChange={e => setCc(e.target.value)} placeholder="name@company.com, other@company.com" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={label} htmlFor="mail-bcc">Bcc</label>
                <input id="mail-bcc" style={input} value={bcc} onChange={e => setBcc(e.target.value)} />
                {!single && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
                    Cc and Bcc are copied onto every individual message.
                  </div>
                )}
              </div>
            </>
          )}

          {templates.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={label} htmlFor="mail-template">Template</label>
              <select id="mail-template" style={input} defaultValue="" onChange={e => applyTemplate(e.target.value)}>
                <option value="">Start from scratch…</option>
                {templates.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
              </select>
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={label} htmlFor="mail-subject">Subject</label>
            <input id="mail-subject" style={input} value={subject} onChange={e => setSubject(e.target.value)} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={label} htmlFor="mail-body">Message</label>
              <button
                type="button"
                style={{ ...ghost, padding: '4px 10px', fontSize: 12 }}
                onClick={async () => {
                  if (showPreview) { setShowPreview(false); return; }
                  try {
                    const r = await mailApi.preview({
                      subject,
                      body: toHtml(single && recipients[0] ? applyMergeFields(body, recipients[0]) : body),
                    });
                    setPreviewHtml(r.data.html);
                    setShowPreview(true);
                  } catch { setPreviewHtml('<p style="font-family:sans-serif;padding:16px;color:var(--danger-fg)">Could not load the preview.</p>'); setShowPreview(true); }
                }}
              >
                {showPreview ? 'Edit' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="eye" size={12} /> Preview</span>}
              </button>
            </div>
            {showPreview ? (
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={previewHtml}
                style={{ width: '100%', minHeight: 340, border: '1px solid var(--border)', borderRadius: 8, background: '#f4f5f7' }}
              />
            ) : (
              <textarea id="mail-body" style={{ ...input, minHeight: 200, resize: 'vertical' }} value={body} onChange={e => setBody(e.target.value)} />
            )}
            {!showPreview && (
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
                {single
                  ? '{{first_name}}, {{name}} and {{email}} are filled in before sending.'
                  : '{{first_name}}, {{name}} and {{email}} are filled in for each recipient before sending.'}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={label}>Attachments</label>
            {allowResumeAttachment && single && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 8 }}>
                <input type="checkbox" checked={attachResume} onChange={e => setAttachResume(e.target.checked)} />
                Attach this candidate’s resume
              </label>
            )}
            <input
              ref={fileRef}
              type="file"
              multiple
              style={{ fontSize: 12 }}
              onChange={e => setFiles(Array.from(e.target.files ?? []))}
            />
            {files.length > 0 && (
              <div style={{ fontSize: 12, color: overLimit ? 'var(--danger-fg)' : 'var(--text-muted)', marginTop: 6 }}>
                {files.length} file{files.length === 1 ? '' : 's'} · {(totalAttachmentBytes / 1024 / 1024).toFixed(1)} MB
                {overLimit && ' — over the 3.0 MB limit for a single message. Remove one, or share a link instead.'}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <button style={ghost} onClick={onClose}>Cancel</button>
            <button
              style={{ ...btn, opacity: sending || overLimit || !status?.connected ? .55 : 1 }}
              disabled={sending || overLimit || !status?.connected}
              onClick={send}
            >
              {sending ? 'Sending…' : single ? 'Send' : `Send ${recipients.length} emails`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
